import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { COMPANY } from "@/data/content";
import { ORG_ID } from "@/data/schema";

const WHAT_WE_DO = [
  { t: "Marketplace account management", d: "Daily operations, listings, pricing, ads, returns and account health on Amazon, Flipkart, Meesho, JioMart and premium marketplaces.", to: "/services/" },
  { t: "Seller onboarding", d: "Registration, documents, category approvals and first listings on 10+ Indian marketplaces and quick-commerce apps.", to: "/marketplaces/" },
  { t: "Quick commerce", d: "Blinkit, Zepto and Instamart vendor onboarding, pack-size and pricing strategy and dark-store inventory planning.", to: "/quick-commerce/" },
  { t: "Listings, A+ Content and video", d: "Keyword-led titles and bullets, A+ Content, infographics and product videos.", to: "/services/a-plus-listing-graphics/" },
  { t: "Websites and apps", d: "Shopify, WooCommerce and custom storefronts and mobile apps, connected to your marketplace catalogues.", to: "/web-app-development/" },
  { t: "USA and UAE marketplaces", d: "Amazon.com, Walmart Marketplace, Amazon.ae and Noon for local brands and Indian brands expanding abroad.", to: "/ecommerce-services-usa/" },
];

export default function About() {
  const aboutSchema = {
    "@type": "AboutPage",
    "@id": "https://orinaenterprise.com/about/#aboutpage",
    url: "https://orinaenterprise.com/about/",
    name: "About Orina Enterprise",
    mainEntity: { "@id": ORG_ID },
  };

  return (
    <>
      <SEO path="/about" schema={aboutSchema} breadcrumbs={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]} />

      <section className="border-b-2 border-black bg-[#FFD700] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">About us</div>
          <h1 className="display mt-6 max-w-4xl text-4xl font-black leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
            About Orina Enterprise
          </h1>
          <p className="mt-5 max-w-3xl text-base text-black/85 md:text-lg">
            Orina Enterprise is an e-commerce agency based in Mumbai, India. We launch, manage and grow brands on online marketplaces and quick-commerce apps, and build the websites and apps that sit alongside them, for brands in India, the USA and the UAE.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">What we do</h2>
          <p className="mt-4 text-base leading-relaxed text-black/85 md:text-lg">
            Most brands sell on several marketplaces at once, each with its own rules, fees, ad tools and sale calendar. We act as the brand's e-commerce team across all of them: one account manager, one weekly report, and specialists for listings, ads, creative and account health behind them.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {WHAT_WE_DO.map((w) => (
              <Link key={w.t} to={w.to} className="card-brutal block hover:bg-[#FFD700]">
                <h3 className="display text-xl font-black">{w.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-black/75">{w.d}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">How we work</h2>
          <ol className="mt-6 list-decimal space-y-3 pl-6 text-base leading-relaxed text-black/85 md:text-lg">
            <li><strong>Free audit:</strong> we review your accounts, listings, ads and margins, or your launch plan if you are new.</li>
            <li><strong>Plan:</strong> we agree priorities, marketplaces and targets for the first 90 days.</li>
            <li><strong>Execution:</strong> your account manager runs daily operations with our listing, ads and creative teams.</li>
            <li><strong>Reporting:</strong> every week you get sales, ad spend and returns by channel, and what we are doing next.</li>
          </ol>
          <p className="mt-6 text-base text-black/85">
            Read client feedback on our <Link to="/testimonials/" className="font-bold underline decoration-[#FFD700] decoration-2 underline-offset-4">testimonials page</Link> or browse answers to common questions in the <Link to="/faq/" className="font-bold underline decoration-[#FFD700] decoration-2 underline-offset-4">FAQ</Link>.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">Where we are</h2>
          <ul className="mt-6 space-y-4 text-base md:text-lg">
            <li className="flex gap-3"><MapPin className="mt-1 shrink-0" size={20} /> {COMPANY.address}</li>
            <li className="flex gap-3"><Phone className="mt-1 shrink-0" size={20} /> Call: <a href={`tel:${COMPANY.phoneTel}`} className="font-bold underline">{COMPANY.phone}</a></li>
            <li className="flex gap-3"><MessageCircle className="mt-1 shrink-0" size={20} /> WhatsApp: <a href={`https://wa.me/${COMPANY.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="font-bold underline">{COMPANY.whatsappDisplay}</a></li>
            <li className="flex gap-3"><Mail className="mt-1 shrink-0" size={20} /> <a href={`mailto:${COMPANY.email}`} className="font-bold underline">{COMPANY.email}</a></li>
          </ul>
          <p className="mt-6 text-base text-black/85">Office hours: Monday to Saturday, 10:00 to 19:00 IST. We schedule calls with US and UAE clients in their business hours.</p>
          <Link to="/contact/" className="btn-brutal mt-8 inline-flex">Contact us <ArrowUpRight size={16} /></Link>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
